import { invertObj, merge, prop, propOr } from 'ramda';

import {
  getRouteKeyForPath,
  getRouteKeyOfChild,
  getUrlForRouteKey,
  isColdPage,
  ROUTE_KEY,
} from '../../config/routes';
import CHILD_ROUTE from '../../config/routes/child-route';
import { REGION_ROUTE_KEY } from '../../config/routes/route';
import { DocumentType } from '../../types/document';
import { MEDIA_TYPE } from '../../types/press-media';
import { path, pathOr } from '../compact';
import { getLang } from '../i18n';
import {
  INTERNATIONAL,
  REGION,
  REGION_REGIONAL,
} from './../../types/filters/region';

const articleRoute = ({ lang, uid } = {}) => {
  const lng = getLang(lang);
  if (isColdPage(uid)) {
    const routeKey = getRouteKeyForPath(`/:lng(${lng})/:articleUid(${uid})`);
    return (
      routeKey && {
        routeKey,
        params: { lng: lng, articleUid: uid },
      }
    );
  }

  return {
    routeKey: ROUTE_KEY.ARTICLE,
    params: { lng: lng, articleUid: uid },
  };
};

const childRoute = (
  paramKey,
  childRouteKey,
  defaultParentRouteKey,
  params = {}
) => ({ uid, lang }, { match: { path } = {} } = {}) => {
  const routeKey = getRouteKeyForPath(path);
  return {
    routeKey: getRouteKeyOfChild(
      defaultParentRouteKey,
      childRouteKey,
      routeKey
    ),
    params: merge({ lng: getLang(lang), [paramKey]: uid }, params),
  };
};

const contactRoute = childRoute(
  'contactUid',
  CHILD_ROUTE.CONTACT.id,
  ROUTE_KEY.CONTACT_PAGE
);
const rewardedInnovationRoute = childRoute(
  'rewardedInnovationUid',
  CHILD_ROUTE.REWARDED_INNOVATION.id,
  ROUTE_KEY.REWARDED_INNOVATION_PAGE
);

const getDefaultRouteOfMedia = ({ mediaType, onNewsroom, onFinance }) => {
  if (onFinance) {
    return mediaType === MEDIA_TYPE.PUBLICATION
      ? ROUTE_KEY.FINANCIAL_PUBLICATION_YEAR
      : ROUTE_KEY.FINANCIAL_PRESS_RELEASE_YEAR;
  }

  if (onNewsroom) {
    return ROUTE_KEY.NEWSROOM;
  }

  return ROUTE_KEY.NEWSROOM;
};

const getRouteParamsForMedia = ({ onNewsroom, onFinance, relatedDate }) => {
  if (onFinance) {
    return { year: new Date(relatedDate).getFullYear() };
  }

  if (onNewsroom) {
    return {};
  }

  return {};
};

const mediaPressRoute = (media, routeInfo) =>
  childRoute(
    'mediaUid',
    CHILD_ROUTE.MEDIA.id,
    getDefaultRouteOfMedia(media),
    getRouteParamsForMedia(media)
  )(media, routeInfo);

const DOC_TO_ROUTE = {
  [DocumentType.ARTICLE]: articleRoute,
  [DocumentType.CONTACT]: contactRoute,
  [DocumentType.MEDIA_PRESS]: mediaPressRoute,
  [DocumentType.REWARDED_INNOVATION]: rewardedInnovationRoute,
  [DocumentType.AGENDA]: () => ({ routeKey: ROUTE_KEY.AGENDA }),
  [DocumentType.NEWSROOM]: () => ({ routeKey: ROUTE_KEY.NEWSROOM }),
  [DocumentType.GOUVERNANCE]: () => ({ routeKey: ROUTE_KEY.GOUVERNANCE }),
  [DocumentType.BRANCH_ACTIVITIES]: () => ({
    routeKey: ROUTE_KEY.BRANCH_ACTIVITIES,
  }),
  [DocumentType.FINANCIAL_DEBT]: () => ({ routeKey: ROUTE_KEY.FINANCIAL_DEBT }),
  [DocumentType.FINANCIAL_RATING]: () => ({
    routeKey: ROUTE_KEY.FINANCIAL_RATING,
  }),
  [DocumentType.FINANCIAL_PUBLICATION]: (doc, routeInfo) => ({
    routeKey:
      path('match.params.year', routeInfo) &&
      path('match.params.year', routeInfo) != new Date(Date.now()).getFullYear()
        ? ROUTE_KEY.FINANCIAL_PUBLICATION_YEAR
        : ROUTE_KEY.FINANCIAL_PUBLICATION,
  }),
  [DocumentType.FINANCIAL_KEY_FIGURES]: () => ({
    routeKey: ROUTE_KEY.FINANCIAL_KEY_FIGURES,
  }),
  [DocumentType.FINANCIAL_PRESS_RELEASE]: (doc, routeInfo) => ({
    routeKey:
      path('match.params.year', routeInfo) &&
      path('match.params.year', routeInfo) != new Date(Date.now()).getFullYear()
        ? ROUTE_KEY.FINANCIAL_PRESS_RELEASE_YEAR
        : ROUTE_KEY.FINANCIAL_PRESS_RELEASE,
  }),
  [DocumentType.HOME]: (doc, routeInfo) => {
    const regionSlug = path('params.regionSlug', routeInfo);
    return {
      routeKey: regionSlug ? ROUTE_KEY[`HOME-${regionSlug}`] : ROUTE_KEY.HOME,
    };
  },
  [DocumentType.PUBLIC_SERVICE_MISSIONS]: () => ({
    routeKey: ROUTE_KEY.PUBLIC_SERVICE_MISSIONS,
  }),
  [DocumentType.POSTMEN]: () => ({ routeKey: ROUTE_KEY.POSTMEN }),
  [DocumentType.GROUP_HISTORY]: () => ({ routeKey: ROUTE_KEY.GROUP_HISTORY }),
  [DocumentType.CONTACT_PAGE]: () => ({ routeKey: ROUTE_KEY.CONTACT_PAGE }),
  [DocumentType.INNOVATION_BY_LA_POSTE]: () => ({
    routeKey: ROUTE_KEY.INNOVATION_BY_LA_POSTE,
  }),
  [DocumentType.SEARCH_PAGE]: () => ({ routeKey: ROUTE_KEY.SEARCH }),
  [DocumentType.REWARDED_INNOVATION_PAGE]: () => ({
    routeKey: ROUTE_KEY.REWARDED_INNOVATION_PAGE,
  }),
  [DocumentType.OUR_BRANDS]: () => ({ routeKey: ROUTE_KEY.OUR_BRANDS }),
};

export const getRouteKeyFromDocument = (doc, routeInfo) => {
  const docType = prop('type', doc);
  const docToRouteFn = propOr(() => null, docType, DOC_TO_ROUTE);
  return docToRouteFn(doc, routeInfo);
};

export const getUrlFromDocument = (doc, routeInfo, keepFilter = false) => {
  const route = getRouteKeyFromDocument(doc, routeInfo);

  if (!route) {
    return '#';
  }

  const { routeKey, params = {} } = route;
  const lang = getLang(prop('lang', doc));
  const routeParams = pathOr({}, 'match.params', routeInfo);
  return [
    getUrlForRouteKey(routeKey, lang, { ...routeParams, ...params }),
    keepFilter && path('location.search', routeInfo),
  ]
    .filter(Boolean)
    .join('');
};

export const TYPE_TO_ROUTE_REGION = {
  [REGION_REGIONAL.AUVERGNE]: REGION_ROUTE_KEY.AUVERGNE,
  [REGION_REGIONAL.BOURGOGNE]: REGION_ROUTE_KEY.BOURGOGNE,
  [REGION_REGIONAL.BRETAGNE]: REGION_ROUTE_KEY.BRETAGNE,
  [REGION_REGIONAL.CENTRE]: REGION_ROUTE_KEY.CENTRE,
  [REGION_REGIONAL.CORSE]: REGION_ROUTE_KEY.CORSE,
  [REGION_REGIONAL.DOM]: REGION_ROUTE_KEY.DOM,
  [REGION_REGIONAL.GRAND_EST]: REGION_ROUTE_KEY.GRAND_EST,
  [REGION_REGIONAL.HAUTS_DE_FRANCE]: REGION_ROUTE_KEY.HAUTS_DE_FRANCE,
  [REGION_REGIONAL.ILE_DE_FRANCE]: REGION_ROUTE_KEY.ILE_DE_FRANCE,
  [REGION.NATIONAL]: REGION_ROUTE_KEY.NATIONAL,
  [REGION_REGIONAL.NORMANDIE]: REGION_ROUTE_KEY.NORMANDIE,
  [REGION_REGIONAL.NOUVELLE_AQUITAINE]: REGION_ROUTE_KEY.NOUVELLE_AQUITAINE,
  [REGION_REGIONAL.OCCITANIE]: REGION_ROUTE_KEY.OCCITANIE,
  [REGION_REGIONAL.PAYS_DE_LA_LOIRE]: REGION_ROUTE_KEY.PAYS_DE_LA_LOIRE,
  [REGION_REGIONAL.PACA]: REGION_ROUTE_KEY.PACA,
  [INTERNATIONAL]: REGION_ROUTE_KEY.INTERNATIONAL,
};

export const routeToTypeRegion = (route) =>
  invertObj(TYPE_TO_ROUTE_REGION)[route];
